import React, { useState, useCallback, useMemo } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';

// ====================================================================
// Mock Data Store & Constants
// ====================================================================

const initialOvertimeRules = [
    {
        id: 'OT001',
        name: 'Standard OT Policy',
        threshold: '30 min',
        rate: '1.5x Hourly Pay',
        requiresApproval: true,
        appliesTo: 'Production',
    },
    {
        id: 'OT002',
        name: 'Project OT',
        threshold: '0 min',
        rate: 'Flat Rate $20/hr',
        requiresApproval: false,
        appliesTo: 'Development',
    },
];

const initialLateRules = [
    {
        id: 'LR001',
        name: 'Standard Lateness',
        gracePeriod: '10 min',
        penaltyType: 'Warning/Day Off',
        accumulation: '3 instances = 1/2 day leave deduction',
    },
    {
        id: 'LR002',
        name: 'Early Exit Policy',
        gracePeriod: '5 min',
        penaltyType: 'Time deducted from pay',
        accumulation: 'Direct deduction',
    },
];

const penaltyOptions = [
    'None',
    'Warning/Day Off',
    'Half-Day Leave Deduction',
    'Full-Day Leave Deduction',
    'Time deducted from pay',
];

// ====================================================================
// 1. OvertimeRuleFormModal Component (Nested)
// ====================================================================

const OvertimeRuleFormModal = ({ isOpen, onClose, onSave, rule }) => {
    const isNew = useMemo(() => rule === null, [rule]);
    const [ruleData, setRuleData] = useState(() => ({
        id: rule?.id || null,
        name: rule?.name || '',
        threshold: rule?.threshold || '30 min',
        rate: rule?.rate || '1.5x Hourly Pay',
        requiresApproval: rule?.requiresApproval || true,
        appliesTo: rule?.appliesTo || 'All Employees',
    }));

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setRuleData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!ruleData.name.trim() || !ruleData.threshold.trim() || !ruleData.rate.trim()) {
            alert('Please fill in all required fields.');
            return;
        }
        onSave(ruleData, isNew);
    };

    return (
        <Transition appear show={isOpen} as={React.Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={React.Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={React.Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel 
                                className="w-full max-w-lg transform overflow-hidden rounded-lg bg-white p-6 text-left shadow-2xl transition-all"
                                style={{ fontFamily: 'Roboto, sans-serif' }}
                            >
                                <Dialog.Title 
                                    as="h3" 
                                    className="text-lg font-semibold leading-6 text-gray-800 border-b pb-3 flex justify-between items-center"
                                    style={{ fontFamily: 'Poppins, sans-serif' }}
                                >
                                    {isNew ? 'Create Overtime Rule' : `Edit OT Rule: ${ruleData.name}`}
                                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-full transition">
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                </Dialog.Title>

                                <form onSubmit={handleSubmit} className="mt-4 space-y-4 text-sm">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="name" className="block text-xs font-medium text-gray-700">Rule Name</label>
                                            <input
                                                type="text"
                                                name="name"
                                                id="name"
                                                value={ruleData.name}
                                                onChange={handleInputChange}
                                                required
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="appliesTo" className="block text-xs font-medium text-gray-700">Applies To</label>
                                            <input
                                                type="text"
                                                name="appliesTo"
                                                id="appliesTo"
                                                value={ruleData.appliesTo}
                                                onChange={handleInputChange}
                                                placeholder="e.g., Engineering, All Staff"
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="threshold" className="block text-xs font-medium text-gray-700">OT Start Threshold</label>
                                            <input
                                                type="text"
                                                name="threshold"
                                                id="threshold"
                                                value={ruleData.threshold}
                                                onChange={handleInputChange}
                                                placeholder="e.g., 30 min after shift end"
                                                required
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="rate" className="block text-xs font-medium text-gray-700">OT Rate/Multiplier</label>
                                            <input
                                                type="text"
                                                name="rate"
                                                id="rate"
                                                value={ruleData.rate}
                                                onChange={handleInputChange}
                                                placeholder="e.g., 1.5x, 2x, Flat Rate"
                                                required
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center pt-2">
                                        <input
                                            id="requiresApproval"
                                            name="requiresApproval"
                                            type="checkbox"
                                            checked={ruleData.requiresApproval}
                                            onChange={handleInputChange}
                                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        <label htmlFor="requiresApproval" className="ml-2 block text-sm text-gray-700">
                                            Requires Manager Approval before payout
                                        </label>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end space-x-3">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700"
                                        >
                                            {isNew ? 'Create Rule' : 'Save Changes'}
                                        </button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

// ====================================================================
// 2. LateRuleFormModal Component (Nested)
// ====================================================================

const LateRuleFormModal = ({ isOpen, onClose, onSave, rule }) => {
    const isNew = useMemo(() => rule === null, [rule]);
    const [ruleData, setRuleData] = useState(() => ({
        id: rule?.id || null,
        name: rule?.name || '',
        gracePeriod: rule?.gracePeriod || '10 min',
        penaltyType: rule?.penaltyType || 'Warning/Day Off',
        accumulation: rule?.accumulation || '3 instances = 1/2 day leave deduction',
    }));

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setRuleData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!ruleData.name.trim() || !ruleData.gracePeriod.trim()) {
            alert('Please fill in the rule name and grace period.');
            return;
        }
        onSave(ruleData, isNew);
    };

    return (
        <Transition appear show={isOpen} as={React.Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={React.Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={React.Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel 
                                className="w-full max-w-lg transform overflow-hidden rounded-lg bg-white p-6 text-left shadow-2xl transition-all"
                                style={{ fontFamily: 'Roboto, sans-serif' }}
                            >
                                <Dialog.Title 
                                    as="h3" 
                                    className="text-lg font-semibold leading-6 text-gray-800 border-b pb-3 flex justify-between items-center"
                                    style={{ fontFamily: 'Poppins, sans-serif' }}
                                >
                                    {isNew ? 'Create Late/Early Out Rule' : `Edit Rule: ${ruleData.name}`}
                                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-full transition">
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                </Dialog.Title>

                                <form onSubmit={handleSubmit} className="mt-4 space-y-4 text-sm">
                                    <div>
                                        <label htmlFor="name" className="block text-xs font-medium text-gray-700">Rule Name (e.g., Standard Lateness, Early Exit)</label>
                                        <input
                                            type="text"
                                            name="name"
                                            id="name"
                                            value={ruleData.name}
                                            onChange={handleInputChange}
                                            required
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                        />
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="gracePeriod" className="block text-xs font-medium text-gray-700">Grace Period Allowed</label>
                                            <input
                                                type="text"
                                                name="gracePeriod"
                                                id="gracePeriod"
                                                value={ruleData.gracePeriod}
                                                onChange={handleInputChange}
                                                placeholder="e.g., 5 min, 15 min"
                                                required
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="penaltyType" className="block text-xs font-medium text-gray-700">Primary Penalty Type</label>
                                            <select
                                                name="penaltyType"
                                                id="penaltyType"
                                                value={ruleData.penaltyType}
                                                onChange={handleInputChange}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            >
                                                {penaltyOptions.map(opt => (
                                                    <option key={opt} value={opt}>{opt}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="accumulation" className="block text-xs font-medium text-gray-700">Accumulation Rule/Details</label>
                                        <textarea
                                            name="accumulation"
                                            id="accumulation"
                                            value={ruleData.accumulation}
                                            onChange={handleInputChange}
                                            rows="2"
                                            placeholder="e.g., 3 instances = 1/2 day leave deduction, or Direct deduction"
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm p-2"
                                        />
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end space-x-3">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700"
                                        >
                                            {isNew ? 'Create Rule' : 'Save Changes'}
                                        </button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};


// ====================================================================
// 3. OvertimeLateRulesSetup (Main Component)
// ====================================================================

const OvertimeLateRulesSetup = () => {
    // State for both sets of rules
    const [otRules, setOtRules] = useState(initialOvertimeRules);
    const [lateRules, setLateRules] = useState(initialLateRules);
    
    // Modal State
    const [isOtModalOpen, setIsOtModalOpen] = useState(false);
    const [isLateModalOpen, setIsLateModalOpen] = useState(false);
    
    const [currentOtRule, setCurrentOtRule] = useState(null);
    const [currentLateRule, setCurrentLateRule] = useState(null);

    // --- Overtime Rule Handlers ---
    const handleCreateOtRule = useCallback(() => {
        setCurrentOtRule(null);
        setIsOtModalOpen(true);
    }, []);

    const handleEditOtRule = useCallback((rule) => {
        setCurrentOtRule(rule);
        setIsOtModalOpen(true);
    }, []);

    const handleSaveOtRule = useCallback((newRuleData, isNew) => {
        if (isNew) {
            setOtRules(prev => [...prev, { 
                ...newRuleData, 
                id: `OT${String(prev.length + 1).padStart(3, '0')}`
            }]);
        } else {
            setOtRules(prev => prev.map(r => 
                r.id === newRuleData.id ? newRuleData : r
            ));
        }
        setIsOtModalOpen(false);
    }, []);

    const handleDeleteOtRule = useCallback((id) => {
        if (window.confirm('Are you sure you want to delete this Overtime Rule?')) {
            setOtRules(prev => prev.filter(r => r.id !== id));
        }
    }, []);

    // --- Late Rule Handlers ---
    const handleCreateLateRule = useCallback(() => {
        setCurrentLateRule(null);
        setIsLateModalOpen(true);
    }, []);

    const handleEditLateRule = useCallback((rule) => {
        setCurrentLateRule(rule);
        setIsLateModalOpen(true);
    }, []);

    const handleSaveLateRule = useCallback((newRuleData, isNew) => {
        if (isNew) {
            setLateRules(prev => [...prev, { 
                ...newRuleData, 
                id: `LR${String(prev.length + 1).padStart(3, '0')}`
            }]);
        } else {
            setLateRules(prev => prev.map(r => 
                r.id === newRuleData.id ? newRuleData : r
            ));
        }
        setIsLateModalOpen(false);
    }, []);

    const handleDeleteLateRule = useCallback((id) => {
        if (window.confirm('Are you sure you want to delete this Late/Early Out Rule?')) {
            setLateRules(prev => prev.filter(r => r.id !== id));
        }
    }, []);

    return (
        <div 
            className="min-h-screen p-4 sm:p-6 lg:p-8 bg-gray-50"
            style={{ fontFamily: 'Roboto, sans-serif' }}
        >
            <header className="flex justify-between items-center pb-6 border-b border-gray-200">
                <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Overtime & Lateness Rules Setup
                </h1>
            </header>

            {/* ---------------------------------------------------- */}
            {/* 1. Overtime Rules Section */}
            {/* ---------------------------------------------------- */}
            <div className="mt-8">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-semibold text-gray-700" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        Overtime Rules ({otRules.length})
                    </h2>
                    <button
                        onClick={handleCreateOtRule}
                        className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow-md hover:bg-indigo-700 transition"
                    >
                        <PlusIcon className="w-5 h-5" />
                        <span>Add OT Rule</span>
                    </button>
                </div>

                {/* Overtime Rules Table */}
                <div className="bg-white shadow-lg rounded-xl overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                {['Rule Name', 'Threshold', 'Rate', 'Requires Approval', 'Applies To', 'Actions'].map(header => (
                                    <th
                                        key={header}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        style={{ fontFamily: 'Roboto, sans-serif' }}
                                    >
                                        {header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {otRules.map((rule) => (
                                <tr key={rule.id} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4 font-medium text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>{rule.name}</td>
                                    <td className="px-6 py-4 text-gray-700">{rule.threshold}</td>
                                    <td className="px-6 py-4 text-gray-700">{rule.rate}</td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${rule.requiresApproval ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                                            {rule.requiresApproval ? 'Yes' : 'No'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-gray-500">{rule.appliesTo}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                                        <button onClick={() => handleEditOtRule(rule)} className="text-indigo-600 hover:text-indigo-900 p-1 rounded-md hover:bg-indigo-50">
                                            <PencilIcon className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => handleDeleteOtRule(rule.id)} className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50">
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {otRules.length === 0 && (
                                <tr><td colSpan="6" className="px-6 py-10 text-center text-gray-500 italic">No overtime rules defined.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ---------------------------------------------------- */}
            {/* 2. Late & Early Out Rules Section */}
            {/* ---------------------------------------------------- */}
            <div className="mt-12">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-semibold text-gray-700" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        Late & Early Out Rules ({lateRules.length})
                    </h2>
                    <button
                        onClick={handleCreateLateRule}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg shadow-md hover:bg-green-700 transition"
                    >
                        <PlusIcon className="w-5 h-5" />
                        <span>Add Late Rule</span>
                    </button>
                </div>

                {/* Late Rules Table */}
                <div className="bg-white shadow-lg rounded-xl overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                {['Rule Name', 'Grace Period', 'Primary Penalty', 'Accumulation Rule', 'Actions'].map(header => (
                                    <th
                                        key={header}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        style={{ fontFamily: 'Roboto, sans-serif' }}
                                    >
                                        {header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {lateRules.map((rule) => (
                                <tr key={rule.id} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4 font-medium text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>{rule.name}</td>
                                    <td className="px-6 py-4 text-gray-700">{rule.gracePeriod}</td>
                                    <td className="px-6 py-4 text-gray-700">{rule.penaltyType}</td>
                                    <td className="px-6 py-4 text-gray-500 max-w-sm overflow-hidden text-ellipsis">{rule.accumulation}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                                        <button onClick={() => handleEditLateRule(rule)} className="text-indigo-600 hover:text-indigo-900 p-1 rounded-md hover:bg-indigo-50">
                                            <PencilIcon className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => handleDeleteLateRule(rule.id)} className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50">
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {lateRules.length === 0 && (
                                <tr><td colSpan="5" className="px-6 py-10 text-center text-gray-500 italic">No late or early out rules defined.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Overtime Rule Modal */}
            {isOtModalOpen && (
                <OvertimeRuleFormModal
                    isOpen={isOtModalOpen}
                    onClose={() => setIsOtModalOpen(false)}
                    onSave={handleSaveOtRule}
                    rule={currentOtRule}
                />
            )}

            {/* Late Rule Modal */}
            {isLateModalOpen && (
                <LateRuleFormModal
                    isOpen={isLateModalOpen}
                    onClose={() => setIsLateModalOpen(false)}
                    onSave={handleSaveLateRule}
                    rule={currentLateRule}
                />
            )}
        </div>
    );
};

export default OvertimeLateRulesSetup;